/**
 * Inline call-log quick-action button + modal.
 * Mounts inside the Lead drawer header (right next to Edit).
 *
 * One submit posts /api/dealer/lead/{id}/call-log which:
 *  - logs to timeline
 *  - bumps contacted=true
 *  - auto-closes any open call_back tasks
 *  - creates a follow-up task if `next_step` was given
 */
import { useState } from "react";
import { Phone, X, Loader2, Check } from "lucide-react";
import { api } from "../lib/api";

const OUTCOMES = ["Reached", "Voicemail", "No Answer", "Busy", "Wrong Number", "Cancelled"];

export default function CallLogQuickAction({ leadId, onLogged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    outcome: "Reached",
    duration_min: "",
    summary: "",
    next_step: "",
    next_step_due_hours: 24,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post(`/dealer/lead/${leadId}/call-log`, {
        outcome: form.outcome,
        duration_min: form.duration_min ? Number(form.duration_min) : null,
        summary: form.summary || null,
        next_step: form.next_step || null,
        next_step_due_hours: form.next_step_due_hours ? Number(form.next_step_due_hours) : 24,
        mark_contacted: true,
      });
      setDone(true);
      onLogged?.();
      setTimeout(() => {
        setOpen(false);
        setDone(false);
        setForm({ outcome: "Reached", duration_min: "", summary: "", next_step: "", next_step_due_hours: 24 });
      }, 1100);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Couldn't save.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-[11px] font-extrabold text-white bg-emerald-600 hover:bg-emerald-700 px-2.5 py-1.5 rounded-full transition"
        data-testid="call-log-open"
      >
        <Phone size={11} /> Just got off the call
      </button>

      {open && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-3" role="dialog" data-testid="call-log-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => !busy && setOpen(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-md shadow-2xl p-5 z-[111]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Phone size={18} className="text-emerald-600" />
                <h3 className="text-lg font-black text-[var(--ssc-navy)]">Log this call</h3>
              </div>
              <button onClick={() => !busy && setOpen(false)} className="text-[#6b7c93] hover:text-[var(--ssc-navy)] p-1" data-testid="call-log-close">
                <X size={20} />
              </button>
            </div>
            {done ? (
              <div className="flex items-center gap-2 text-emerald-700 font-bold text-sm py-6 justify-center" data-testid="call-log-done">
                <Check size={18} /> Logged. Timeline updated.
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="call-log-form">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Outcome">
                    <select className="ssc-select" value={form.outcome} onChange={set("outcome")} data-testid="call-log-outcome">
                      {OUTCOMES.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Duration (min)">
                    <input className="ssc-input" inputMode="numeric" value={form.duration_min} onChange={set("duration_min")} placeholder="e.g. 12" data-testid="call-log-duration" />
                  </Field>
                </div>
                <Field label="What was said">
                  <textarea className="ssc-input min-h-[80px]" value={form.summary} onChange={set("summary")} placeholder="Pre-approved at 5/3, wants Saturday 10am test drive…" data-testid="call-log-summary" />
                </Field>
                <Field label="Next step (creates a follow-up task)">
                  <input className="ssc-input" value={form.next_step} onChange={set("next_step")} placeholder="e.g. Confirm test drive Saturday" data-testid="call-log-next" />
                </Field>
                {form.next_step && (
                  <Field label="Follow up in (hours)">
                    <input className="ssc-input" type="number" min={1} max={720} value={form.next_step_due_hours} onChange={set("next_step_due_hours")} data-testid="call-log-due" />
                  </Field>
                )}
                {err && <div className="text-sm text-red-600" data-testid="call-log-err">{err}</div>}
                <div className="flex gap-2 pt-1">
                  <button type="submit" disabled={busy} className="ssc-btn ssc-btn-primary flex-1 inline-flex items-center justify-center gap-1.5" data-testid="call-log-submit">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Log call
                  </button>
                  <button type="button" onClick={() => setOpen(false)} disabled={busy} className="ssc-btn ssc-btn-secondary" data-testid="call-log-cancel">Cancel</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="ssc-label">{label}</label>
    {children}
  </div>
);
